import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import ContentCaption from './content_caption';
import {
  componentIsInRangeOfViewport,
  throttle
} from '../../utils/dom';
import {
  getJWPlayerContainerID,
  isJWVideoPlaying,
  loadOrInitializeJWPlayer,
  pauseJWPlayer,
  removeJWPlayer
} from '../../utils/video/jw';
import { captureMessage } from '../../utils/browserSentry';
import withEscapeHatch from '../hocs/withEscapeHatch';
import { JW_READY_HANDLER_TIMEOUT } from '../../constants';
import {some} from 'lodash';

const CONDENSED_HEADER_HEIGHT_PX = 60;

export class InternalVideoSection extends Component {
  constructor(props, context) {
    super(props, context);
    this.state = { isPlaying: false };
    this.handleScroll = throttle(
      this.handleScroll,
      this.props.throttleWait
    );
  }

  componentDidMount() {
    this.loadOrInitializeJWPlayer();
  }

  shouldComponentUpdate = (nextProps) =>
    some(
      ['jw_video_id', 'credit', 'description'],
      value => nextProps.content[value] !== this.props.content[value]
    );

  componentDidUpdate(prevProps) {
    removeJWPlayer(prevProps);
    window.removeEventListener('scroll', this.handleScroll);
    this.loadOrInitializeJWPlayer();
  }

  componentWillUnmount() {
    removeJWPlayer(this.props);
    window.removeEventListener('scroll', this.handleScroll);
  }

  loadOrInitializeJWPlayer = () => {
    const handlers = {
      play: this.handlePlay,
      pause: this.handlePause,
      adPlay: this.handleAdPlay,
      adPause: this.handlePause,
      complete: this.props.completionHandler,
      ready: this.handleReady,
      displayClick: this.props.clickHandler || (() => {})
    };

    loadOrInitializeJWPlayer(
      this.props,
      handlers,
      this.context.edition_data,
      this.context.location
    )
      .then(() => {
      /*
       * We currently don't use the player instance for anything. But in case
       * we need to in the future, we have access to it here.
       * It's the first param in this block.
       */
      });
  }

  /*
   * In case the API is broken, causing `readyHandler` or `playHandler` not to be
   * called, we fire `timeoutHandler` here after a timeout. Also log it to Sentry.
   */
  escapeHatch = () => {
    captureMessage(
      'The JW Player API did not fire one of the "ready", "play", or "adPlay"'
      + ' events within 6 seconds. Check if there is anything wrong with the'
      + ' API integration.'
    );
    this.props.timeoutHandler();
  }

  handleReady = () => {
    if (!this.props.readyHandler) {
      return;
    }
    this.props.readyHandler();
  }

  handlePlay = () => {
    window.addEventListener('scroll', this.handleScroll);

    /*
     * In case of an autoplaying video on a series, let's make sure that we
     * the preview image is closed when the video starts playing.
     * (Note: we currently have JW autoplay disabled site-wide.)
     */
    if (this.props.playHandler) {
      this.props.playHandler();
    }
  }

  handleAdPlay = () => {
    /*
     * In case of an autoplaying video on a series, let's make sure that we
     * the preview image is closed when the video starts playing.
     * (Note: we currently have JW autoplay disabled site-wide.)
     */
    if (this.props.playHandler) {
      this.props.playHandler();
    }
  }

  handlePause = () => {
    window.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll = () => {
    if (!this.shouldPause()) {
      return;
    }
    pauseJWPlayer(this.props);
  }

  isJWVideoVisible = () =>
    componentIsInRangeOfViewport(
      this._node,
      -CONDENSED_HEADER_HEIGHT_PX,
      window
    );

  shouldPause = () =>
    isJWVideoPlaying(this.props) && !this.isJWVideoVisible();

  contentCaption() {
    if (this.props.shouldHideContentCaption) {
      return null;
    }
    const { credit, description } = this.props.content;
    return <ContentCaption credit={credit} description={description} />;
  }

  render() {
    return (
      <div ref={node => this._node = node} className="section-internal-video">
        <div className="video-container" dangerouslySetInnerHTML={{
          __html: `<div id="${getJWPlayerContainerID(this.props)}"></div>`
        }}
        />
        {this.contentCaption()}
      </div>
    );
  }
}

InternalVideoSection.propTypes = {
  adsState: PropTypes.shape({
    ad_data: PropTypes.shape({
      ad_sponsorship: PropTypes.string,
      ad_disable_video_preroll: PropTypes.bool.isRequired,
      ad_is_sensitive_content: PropTypes.bool.isRequired
    })
  }).isRequired,
  content: PropTypes.shape({
    jw_video_id: PropTypes.string.isRequired,
    credit: PropTypes.string,
    description: PropTypes.string,
    asset_id: PropTypes.number,
    entry_asset_id: PropTypes.number,
    alignment: PropTypes.string.isRequired,
    width_percentage: PropTypes.string
  }).isRequired,
  sold_content_type: PropTypes.string,
  url_full: PropTypes.string.isRequired,
  intl: PropTypes.object.isRequired,
  isFirstSection: PropTypes.bool.isRequired,
  isMobileSlideSection: PropTypes.bool.isRequired,
  isDesktopSlideSection: PropTypes.bool,
  sanitizedTitle: PropTypes.string.isRequired,
  shouldHideContentCaption: PropTypes.bool,
  throttleWait: PropTypes.number.isRequired,
  completionHandler: PropTypes.func,
  readyHandler: PropTypes.func,
  timeoutHandler: PropTypes.func,
  clickHandler: PropTypes.func,
  playHandler: PropTypes.func
};

InternalVideoSection.defaultProps = {
  isFirstSection: false,
  isMobileSlideSection: false,
  throttleWait: 500
};

InternalVideoSection.contextTypes = {
  edition_data: PropTypes.shape({
    id: PropTypes.number,
    config: PropTypes.shape({
      jw_player_id: PropTypes.string
    })
  }),
  location: PropTypes.object
};

export default withEscapeHatch(
  {
    timeout: JW_READY_HANDLER_TIMEOUT,
    logEscapeHatch: () => {
      captureMessage(
        'The JW Player API did not fire one of the "ready", "play", or "adPlay"'
        + ' events within 6 seconds. Check if there is anything wrong with the'
        + ' API integration.'
      );
    }
  }
)(injectIntl(InternalVideoSection));
